import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { getCampaignCreationRequests } from '../../api/campaign';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdVisibility } from 'react-icons/md';

const NewCampaignRequestList = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setHeaderTitle('New Campaign Requests');
    fetchRequests();
  }, [setHeaderTitle]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await getCampaignCreationRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching campaign requests:', error);
      toast.error('Failed to load campaign requests');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'active' || statusLower === 'pending') {
      return <span className='px-3 py-1 bg-chipYellow text-white rounded-full text-sm font-semibold'>Pending</span>;
    } else if (statusLower === 'resolved' || statusLower === 'approved') {
      return <span className='px-3 py-1 bg-chipGreen text-white rounded-full text-sm font-semibold'>Approved</span>;
    } else if (statusLower === 'rejected') {
      return <span className='px-3 py-1 bg-chipRed text-white rounded-full text-sm font-semibold'>Rejected</span>;
    }
    return <span className='px-3 py-1 bg-gray-400 text-white rounded-full text-sm font-semibold'>{status || 'Pending'}</span>;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = requests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(requests.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <section className='flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-sm max-md:max-w-full'>
        <div className='flex flex-col pr-px w-full max-md:max-w-full'>
          <div className='flex items-center justify-between pb-3 w-full pr-5'>
            <h1 className='self-start text-base font-bold pl-6'>List of New Campaign Requests</h1>
            <button
              className='font-bold text-sm text-primary hover:text-primaryHoverDark cursor-pointer'
              onClick={() => navigate('/campaigns/new-req/create')}
            >
              + Create New Request
            </button>
          </div>
        </div>
        <div className='flex flex-col items-center justify-center py-16'>
          <div className='text-center py-8 text-gray-500'>
            <p className='text-lg mb-4'>No Campaign Requests Found</p>
            <button
              onClick={() => navigate('/campaigns/new-req/create')}
              className='mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryHoverDark transition-colors'
            >
              Create Your First Request
            </button>
          </div>
        </div>
        <ToastContainer position="bottom-right" autoClose={2000} />
      </section>
    );
  }

  return (
    <section className='flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-sm max-md:max-w-full'>
      <div className='flex flex-col pr-px w-full max-md:max-w-full'>
        <div className='flex items-center justify-between pb-3 w-full pr-5'>
          <h1 className='self-start text-base font-bold pl-6'>List of New Campaign Requests</h1>
          <button
            className='font-bold text-sm text-primary hover:text-primaryHoverDark cursor-pointer'
            onClick={() => navigate('/campaigns/new-req/create')}
          >
            + Create New Request
          </button>
        </div>

        {/* Table */}
        <div className='overflow-x-auto px-6'>
          <table className='w-full'>
            <thead>
              <tr className='border-b border-gray-200'>
                <th className='text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4'>Sr No.</th>
                <th className='text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4'>Campaign Type</th>
                <th className='text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4'>Message</th>
                <th className='text-center text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4'>Duration</th>
                <th className='text-center text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4'>Status</th>
                <th className='text-center text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((request, index) => (
                <tr key={request._id || request.id || index} className='border-b border-gray-100 hover:bg-gray-50'>
                  <td className='py-3 px-4 text-sm text-gray-900'>{indexOfFirstItem + index + 1}</td>
                  <td className='py-3 px-4 text-sm text-gray-900 whitespace-nowrap'>{request.campaignType || 'N/A'}</td>
                  <td className='py-3 px-4 text-sm text-gray-900 max-w-xs truncate'>{request.message || 'N/A'}</td>
                  <td className='py-3 px-4 text-sm text-gray-900 text-center whitespace-nowrap'>
                    {formatDate(request.startDate)} - {formatDate(request.endDate)}
                  </td>
                  <td className='py-3 px-4 text-center'>{getStatusBadge(request.status || (request.active ? 'Active' : 'Resolved'))}</td>
                  <td className='py-3 px-4 text-center'>
                    <button
                      className='rounded p-1.5 bg-primaryBgLight hover:bg-primaryBg mx-1 min-h-7 min-w-7 cursor-pointer inline-flex items-center justify-center'
                      onClick={() => navigate(`/campaigns/new-req/${request._id || request.id}`)}
                      title='View Request'
                    >
                      <MdVisibility className='text-primary text-lg' />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex items-center justify-between px-6 py-4 border-t border-gray-200'>
            <div className='text-sm text-gray-600'>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, requests.length)} of {requests.length} entries
            </div>
            <div className='flex gap-2'>
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded border ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => paginate(index + 1)}
                  className={`px-3 py-1 rounded border ${
                    currentPage === index + 1
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded border ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <ToastContainer position="bottom-right" autoClose={2000} />
    </section>
  );
};

export default NewCampaignRequestList;
